'use client';

import { useState, useEffect, useRef } from 'react';
import { setCookie, hasCookie } from 'cookies-next';
import { motion, AnimatePresence } from 'motion/react';
import Link from 'next/link';

import useDocumentReadyState from 'app/hooks/useDocumentReadyState';
import Portal from 'app/components/common/Portal';
import ImageBg from 'app/components/common/ImageBg';
import {
  defaultProcessText,
  defaultProcessTextWithoutLinks
} from 'app/utility/textProcessing';
import { IImage } from 'app/interfaces/common';
import { CONSENT_COOKIE_NAME, CONSENT_EVENT } from 'app/utility/cookieConsent';

// Через сколько после загрузки страницы показывать баннер (мс).
const SHOW_DELAY = 5000;
// Сколько «помнить» закрытие баннера (сек) — 1 день.
const TIME_TO_EXPIRES = 24 * 60 * 60;
const COOKIE_NAME = 'promo_banner_closed';

interface PromoBannerProps {
  content: string | null;
  image?: IImage | null;
  url?: string | null;
}

/** Оборачивает содержимое в ссылку, если задан url. */
function BannerLink({
  url,
  children
}: { url?: string | null } & React.PropsWithChildren) {
  if (!url) return <>{children}</>;

  return (
    <Link href={url} target="_blank" className="flex">
      {children}
    </Link>
  );
}

export default function PromoBanner({ content, image, url }: PromoBannerProps) {
  const [isShown, setIsShown] = useState(false);
  const readyState = useDocumentReadyState();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Ждём загрузку страницы; не показываем, если баннер уже закрывали.
    if (readyState !== 'complete' || hasCookie(COOKIE_NAME)) return;

    const show = () => {
      timerRef.current = setTimeout(() => setIsShown(true), SHOW_DELAY);
    };

    if (hasCookie(CONSENT_COOKIE_NAME)) {
      // Куки уже приняты ранее — показываем баннер с задержкой.
      show();
    } else {
      // Согласия ещё нет (открыта модалка куки) — ждём его, чтобы баннер и
      // модалка не показывались одновременно.
      window.addEventListener(CONSENT_EVENT, show);
    }

    return () => {
      window.removeEventListener(CONSENT_EVENT, show);
      clearTimeout(timerRef.current || 0);
    };
  }, [readyState]);

  const handleClose = () => {
    setCookie(COOKIE_NAME, 'true', {
      path: '/',
      maxAge: TIME_TO_EXPIRES
    });
    setIsShown(false);
  };

  if (!content) return null;

  return (
    <Portal>
      <AnimatePresence initial={false}>
        {isShown && (
          <motion.div
            data-lenis-prevent
            // TODO: позиционирование/обёртка — стилизовать
            className="fixed bottom-3 md:bottom-5 lg:bottom-6 left-0 z-30 wrapper-main max-w-full"
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              open: { y: 0, opacity: 1, transition: { duration: 0.5 } },
              closed: { y: '10%', opacity: 0, transition: { duration: 0.5 } }
            }}
          >
            {/* TODO: разметка и стили баннера */}
            <div className="relative flex gap-1 min-h-17">
              <BannerLink url={url}>
                <div className="flex bg-white hover:bg-gray transition-colors duration-300 rounded items-center">
                  <div
                    className="p-3 max-w-96 text-p3 md:text-p2 [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      // Когда задан url, весь баннер — это <Link> (<a>), поэтому
                      // ссылки внутри content вырезаем: вложенный <a> в <a> ломает
                      // гидратацию в React 19. Без url ссылки контента сохраняем.
                      __html: url
                        ? defaultProcessTextWithoutLinks(content)
                        : defaultProcessText(content)
                    }}
                  ></div>

                  {!!image?.url && (
                    // TODO: размеры/соотношение сторон под дизайн
                    <div className="relative w-15 md:w-20 h-full min-h-15 md:min-h-20 shrink-0">
                      <ImageBg
                        src={image.url}
                        alt={image.alt || ''}
                        fill
                        placeholderDataURL={image.placeholder_data_url}
                        className="object-cover rounded"
                      />
                    </div>
                  )}
                </div>
              </BannerLink>

              <button
                type="button"
                onClick={handleClose}
                aria-label="Закрыть"
                className="button-default w-16 md:w-20 bg-white hover:bg-gray text-brown border-transparent rounded shrink-0"
              >
                {/* TODO: иконка/стили кнопки закрытия */}
                Закрыть
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
}
